// frontend/src/services/markdownService.js
// Enterprise-grade markdown processing service with security and performance optimizations

import { marked } from 'marked';
import DOMPurify from 'dompurify';

/**
 * Secure markdown processing service
 * Handles markdown-to-HTML conversion with XSS protection and performance optimization
 */
class MarkdownService {
  constructor() {
    this.configureMarked();
    this.setupCache();
  }

  /**
   * Configure marked with security-first settings
   * @private
   */
  configureMarked() {
    marked.setOptions({
      // Security: Disable HTML rendering to prevent XSS
      sanitize: false, // We handle sanitization with DOMPurify
      smartLists: true,
      smartypants: true,
      // Performance: Enable breaks for better UX
      breaks: true,
      gfm: true, // GitHub Flavored Markdown
      // Disable dangerous features
      headerIds: false,
      mangle: false
    });

    // Configure renderer for better control
    const renderer = new marked.Renderer();

    // Override link rendering for security
    renderer.link = (href, title, text) => {
      const cleanHref = DOMPurify.sanitize(href);
      const cleanTitle = title ? DOMPurify.sanitize(title) : '';
      const cleanText = DOMPurify.sanitize(text);

      return `<a href="${cleanHref}" ${cleanTitle ? `title="${cleanTitle}"` : ''} target="_blank" rel="noopener noreferrer">${cleanText}</a>`;
    };

    // Override image rendering for security
    renderer.image = (href, title, text) => {
      const cleanHref = DOMPurify.sanitize(href);
      const cleanTitle = title ? DOMPurify.sanitize(title) : '';
      const cleanText = DOMPurify.sanitize(text);

      return `<img src="${cleanHref}" alt="${cleanText}" ${cleanTitle ? `title="${cleanTitle}"` : ''} style="max-width: 100%; height: auto;">`;
    };

    marked.use({ renderer });
  }

  /**
   * Setup LRU cache for performance optimization
   * @private
   */
  setupCache() {
    this.cache = new Map();
    this.maxCacheSize = 100; // Limit cache size
  }

  /**
   * Convert markdown to safe HTML
   * @param {string} markdown - Raw markdown content
   * @param {Object} options - Conversion options
   * @returns {string} Safe HTML content
   */
  markdownToHtml(markdown, options = {}) {
    if (!markdown || typeof markdown !== 'string') {
      return '';
    }

    // Check cache first
    const cacheKey = `${markdown.substring(0, 100)}:${JSON.stringify(options)}`;
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    try {
      // Convert markdown to HTML
      const rawHtml = marked.parse(markdown);

      // Sanitize HTML to prevent XSS attacks
      const sanitizedHtml = DOMPurify.sanitize(rawHtml, {
        ALLOWED_TAGS: [
          'p', 'br', 'strong', 'em', 'u', 'del', 's',
          'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
          'ul', 'ol', 'li',
          'blockquote', 'code', 'pre',
          'a', 'img',
          'table', 'thead', 'tbody', 'tr', 'th', 'td'
        ],
        ALLOWED_ATTR: [
          'href', 'title', 'target', 'rel',
          'src', 'alt', 'style'
        ],
        ALLOW_DATA_ATTR: false
      });

      // Cache the result
      this.updateCache(cacheKey, sanitizedHtml);

      return sanitizedHtml;
    } catch (error) {
      console.error('MarkdownService: Failed to convert markdown to HTML:', error);
      // Fallback: return plain text
      return DOMPurify.sanitize(markdown);
    }
  }

  /**
   * Extract title from markdown content
   * @param {string} markdown - Raw markdown content
   * @returns {string} Generated title
   */
  extractTitle(markdown) {
    if (!markdown || typeof markdown !== 'string') {
      return '';
    }

    try {
      // Split into lines and find first meaningful content
      const lines = markdown.split('\n').map(line => line.trim()).filter(Boolean);

      if (lines.length === 0) {
        return '';
      }

      const firstLine = lines[0];

      // Remove markdown formatting
      const cleanTitle = firstLine
        .replace(/^#{1,6}\s+/, '') // Remove heading markers
        .replace(/[*_~`]/g, '') // Remove emphasis markers
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Remove link formatting, keep text
        .trim();

      // Limit title length
      const maxLength = 50;
      return cleanTitle.length > maxLength
        ? cleanTitle.substring(0, maxLength).trim() + '...'
        : cleanTitle;
    } catch (error) {
      console.error('MarkdownService: Failed to extract title:', error);
      return '';
    }
  }

  /**
   * Detect if content is markdown vs plain text
   * @param {string} content - Content to analyze
   * @returns {boolean} True if content appears to be markdown
   */
  isMarkdown(content) {
    if (!content || typeof content !== 'string') {
      return false;
    }

    // Common markdown patterns
    const markdownPatterns = [
      /^#{1,6}\s+/, // Headers
      /\*\*[\s\S]*?\*\*/, // Bold
      /\*[\s\S]*?\*/, // Italic
      /`[\s\S]*?`/, // Inline code
      /```[\s\S]*?```/, // Code blocks
      /\[[\s\S]*?\]\([\s\S]*?\)/, // Links
      /^[-*+]\s+/m, // Unordered lists
      /^\d+\.\s+/m, // Ordered lists
      /^>\s+/m // Blockquotes
    ];

    return markdownPatterns.some(pattern => pattern.test(content));
  }

  /**
   * Update cache with LRU eviction
   * @private
   */
  updateCache(key, value) {
    // Remove oldest entry if cache is full
    if (this.cache.size >= this.maxCacheSize) {
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
    }

    this.cache.set(key, value);
  }

  /**
   * Clear cache
   */
  clearCache() {
    this.cache.clear();
  }

  /**
   * Get cache statistics for debugging
   * @returns {Object} Cache statistics
   */
  getCacheStats() {
    return {
      size: this.cache.size,
      maxSize: this.maxCacheSize,
      hitRate: this.cacheHits / (this.cacheHits + this.cacheMisses) || 0
    };
  }
}

// Export singleton instance
export const markdownService = new MarkdownService();

// Export class for testing
export { MarkdownService };

/**
 * Convert TipTap editor content to Markdown
 * @param {Object} editor - TipTap editor instance
 * @returns {string} Markdown text
 */
export const toMarkdown = (editor) => {
  if (!editor) return '';

  // Get HTML from TipTap editor
  const html = editor.getHTML();

  // Simple HTML -> Markdown conversion
  let markdown = html
    // Headings
    .replace(/<h1>(.*?)<\/h1>/g, '# $1\n\n')
    .replace(/<h2>(.*?)<\/h2>/g, '## $1\n\n')
    .replace(/<h3>(.*?)<\/h3>/g, '### $1\n\n')
    .replace(/<h4>(.*?)<\/h4>/g, '####$1\n\n')
    .replace(/<h5>(.*?)<\/h5>/g, '##### $1\n\n')
    .replace(/<h6>(.*?)<\/h6>/g, '###### $1\n\n')

    // Paragraphs
    .replace(/<p>(.*?)<\/p>/gs, '$1\n\n')

    // Bold and Italic
    .replace(/<strong>(.*?)<\/strong>/g, '**$1**')
    .replace(/<b>(.*?)<\/b>/g, '**$1**')
    .replace(/<em>(.*?)<\/em>/g, '*$1*')
    .replace(/<i>(.*?)<\/i>/g, '*$1*')

    // Code
    .replace(/<code>(.*?)<\/code>/g, '`$1`')
    .replace(/<pre><code>(.*?)<\/code><\/pre>/gs, '```\n$1\n```\n\n')

    // Lists
    .replace(/<ul>/g, '')
    .replace(/<\/ul>/g, '\n')
    .replace(/<ol>/g, '')
    .replace(/<\/ol>/g, '\n')
    .replace(/<li>(.*?)<\/li>/g, '- $1\n')

    // Links
    .replace(/<a href="(.*?)">(.*?)<\/a>/g, '[$2]($1)')

    // Images
    .replace(/<img src="(.*?)" alt="(.*?)".*?>/g, '![$2]($1)')
    .replace(/<img src="(.*?)".*?>/g, '![]($1)')

    // Blockquotes
    .replace(/<blockquote>(.*?)<\/blockquote>/gs, (match, content) => {
      return content
        .split('\n')
        .map(line => `> ${line}`)
        .join('\n') + '\n\n';
    })

    // Horizontal rules
    .replace(/<hr\s*\/?>/g, '\n---\n\n')

    // Line breaks
    .replace(/<br\s*\/?>/g, '  \n')

    // Clean up remaining HTML tags
    .replace(/<[^>]+>/g, '')

    // Decode HTML entities
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")

    // Clean up excessive newlines
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  return markdown;
};

/**
 * Convert Markdown to HTML for TipTap editor
 * This uses the existing markdownToHtml method with TipTap-friendly output
 * @param {string} markdown - Markdown text
 * @returns {string} HTML content for TipTap
 */
export const fromMarkdown = (markdown) => {
  return markdownService.markdownToHtml(markdown);
};

// Export commonly used functions
export const {
  markdownToHtml,
  extractTitle,
  isMarkdown
} = markdownService;